import React, { createContext, useContext, useState, useEffect } from 'react';

const PaymentContext = createContext();

export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
};

export const PaymentProvider = ({ children }) => {
  const [subscription, setSubscription] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);

  useEffect(() => {
    // Load mock data from localStorage
    const storedSubscription = localStorage.getItem('bridgecare_subscription');
    const storedHistory = localStorage.getItem('bridgecare_payment_history');
    
    if (storedSubscription) {
      try {
        setSubscription(JSON.parse(storedSubscription));
      } catch (error) {
        console.error('Failed to parse subscription:', error);
      }
    } else {
      // Set a default mock subscription
      const mockSubscription = {
        plan: 'monthly',
        status: 'active',
        amount: 5000,
        startDate: new Date().toISOString(),
        endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        autoRenew: true,
      };
      setSubscription(mockSubscription);
      localStorage.setItem('bridgecare_subscription', JSON.stringify(mockSubscription));
    }
    
    if (storedHistory) {
      try {
        setPaymentHistory(JSON.parse(storedHistory));
      } catch (error) {
        console.error('Failed to parse payment history:', error);
      }
    } else {
      // Set default mock payment history
      const mockHistory = [
        {
          id: '1',
          date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          amount: 5000,
          type: 'subscription',
          status: 'completed',
          description: 'Monthly Subscription - Mother & Child Care',
        },
        {
          id: '2',
          date: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
          amount: 2500,
          type: 'pay-per-service',
          status: 'completed',
          description: 'Antenatal Care Consultation',
        },
      ];
      setPaymentHistory(mockHistory);
      localStorage.setItem('bridgecare_payment_history', JSON.stringify(mockHistory));
    }
  }, []);

  const subscribe = (plan) => {
    const amounts = {
      monthly: 5000,
      yearly: 50000,
    };
    
    const newSubscription = {
      plan,
      status: 'active',
      amount: amounts[plan],
      startDate: new Date().toISOString(),
      endDate: new Date(Date.now() + (plan === 'monthly' ? 30 : 365) * 24 * 60 * 60 * 1000).toISOString(),
      autoRenew: true,
    };
    
    setSubscription(newSubscription);
    localStorage.setItem('bridgecare_subscription', JSON.stringify(newSubscription));
    
    // Add to payment history
    const payment = {
      id: Math.random().toString(36).substr(2, 9),
      date: new Date().toISOString(),
      amount: amounts[plan],
      type: 'subscription',
      status: 'completed',
      description: `${plan === 'monthly' ? 'Monthly' : 'Yearly'} Subscription - Mother & Child Care`,
    };
    
    const updatedHistory = [payment, ...paymentHistory];
    setPaymentHistory(updatedHistory);
    localStorage.setItem('bridgecare_payment_history', JSON.stringify(updatedHistory));
  };

  const payForService = (service) => {
    const payment = {
      id: Math.random().toString(36).substr(2, 9),
      date: new Date().toISOString(),
      amount: service.price,
      type: 'pay-per-service',
      status: 'completed',
      description: service.name,
    };
    
    const updatedHistory = [payment, ...paymentHistory];
    setPaymentHistory(updatedHistory);
    localStorage.setItem('bridgecare_payment_history', JSON.stringify(updatedHistory));
    
    return payment;
  };

  const cancelSubscription = () => {
    if (subscription) {
      const updatedSubscription = {
        ...subscription,
        status: 'cancelled',
        autoRenew: false,
      };
      setSubscription(updatedSubscription);
      localStorage.setItem('bridgecare_subscription', JSON.stringify(updatedSubscription));
    }
  };

  const value = {
    subscription,
    paymentHistory,
    subscribe,
    payForService,
    cancelSubscription,
    hasActiveSubscription: subscription?.status === 'active',
  };

  return <PaymentContext.Provider value={value}>{children}</PaymentContext.Provider>;
};
